import { ArcElement, Chart, PieController } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import PropTypes from 'prop-types'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { badgeColors, colors } from 'theme'

const badges = [
  { type: 'BRONZE', name: 'Badge Bronze', color: badgeColors.bronze, range: 'De 0 à 150 points' },
  { type: 'IRON', name: 'Badge Fer', color: badgeColors.iron, range: 'De 151 à 450 points' },
  { type: 'STEEL', name: 'Badge Acier', color: badgeColors.steel, range: 'De 451 à 900 points' },
  { type: 'SILVER', name: 'Badge Argent', color: badgeColors.silver, range: 'De 901 à 1 500 points' },
  { type: 'QUARTZ', name: 'Badge Quartz', color: badgeColors.quartz, range: 'De 1 501 à 2 300 points' },
  { type: 'OPAL', name: 'Badge Opale', color: badgeColors.opal, range: 'De 2 301 à 3 200 points' },
  { type: 'AGATE', name: 'Badge Agate', color: badgeColors.agate, range: 'De 3 201 à 4 200 points' },
  { type: 'JADE', name: 'Badge Jade', color: badgeColors.jade, range: 'De 4 201 à 5 400 points' },
  { type: 'AMBER', name: 'Badge Ambre', color: badgeColors.amber, range: 'De 5 401 à 6 800 points' },
  { type: 'GOLDEN', name: 'Badge Or', color: badgeColors.gold, range: 'De 6 801 à 8 400 points' },
  { type: 'PLATINUM', name: 'Badge Platine', color: badgeColors.platinum, range: 'De 8 401 à 10 200 points' },
  { type: 'TITANIUM', name: 'Badge Titane', color: badgeColors.titanium, range: 'De 10 201 à 12 200 points' },
  { type: 'TUNGSTEN', name: 'Badge Tungstène', color: badgeColors.tungsten, range: 'De 12 201 à 14 400 points' },
  { type: 'RHODIUM', name: 'Badge Rhodium', color: badgeColors.rhodium, range: 'De 14 401 à 16 800 points' },
  { type: 'IRIDIUM', name: 'Badge Iridium', color: badgeColors.iridium, range: 'De 16 801 à 19 400 points' },
  { type: 'TOPAZ', name: 'Badge Topaze', color: badgeColors.topaz, range: 'De 19 401 à 22 200 points' },
  { type: 'GARNET', name: 'Badge Grenat', color: badgeColors.garnet, range: 'De 22 201 à 25 200 points' },
  { type: 'AMETHYST', name: 'Badge Améthyste', color: badgeColors.amethyst, range: 'De 25 201 à 28 400 points' },
  { type: 'SPINEL', name: 'Badge Spinelle', color: badgeColors.spinel, range: 'De 28 401 à 31 800 points' },
  { type: 'SAPPHIRE', name: 'Badge Saphir', color: badgeColors.sapphire, range: 'De 31 801 à 35 400 points' },
  { type: 'EMERALD', name: 'Badge Émeraude', color: badgeColors.emerald, range: 'De 35 401 à 39 200 points' },
  { type: 'RUBY', name: 'Badge Rubis', color: badgeColors.ruby, range: 'De 39 201 à 43 200 points' },
  { type: 'DIAMOND', name: 'Badge Diamant', color: badgeColors.diamond, range: 'De 43 201 à 47 400 points' },
  { type: 'OBSIDIAN', name: 'Badge Obsidienne', color: badgeColors.obsidian, range: 'De 47 401 à 52 000 points' },
  { type: 'METEORITE', name: 'Badge Méteorite', color: badgeColors.meteorite, range: 'De 52 001 à 57 000 points' },
  { type: 'ADAMANTIUM', name: 'Badge Adamantium', color: badgeColors.adamantium, range: 'De 57 001 à 62 500 points' },
  { type: 'MITHRIL', name: 'Badge Mithril', color: badgeColors.mithril, range: 'De 62 501 à 68 000 points' },
  { type: 'ORICHALCUM', name: 'Badge Orichalque', color: badgeColors.orichalcum, range: 'De 68 001 à 78 000 points' },
  { type: 'NEBULA', name: 'Badge Nébuleuse', color: badgeColors.nebula, range: 'De 78 001 à 90 000 points' },
  { type: 'AETHER', name: 'Badge Éther', color: badgeColors.aether, range: 'Plus de 90 000 points' },
]

const data = {
  type: 'pie',
  plugins: [ChartDataLabels],
  data: {
    labels: [],
    datasets: [
      {
        backgroundColor: [],
        data: [],
      },
    ],
  },
  options: {
    maintainAspectRatio: false,
    responsive: true,
    layout: {
      padding: {
        left: 30,
        right: 30,
        bottom: 30,
        top: 30,
      },
    },
    plugins: {
      datalabels: {
        anchor: 'end',
        align: 'end',
        display: ctx => ctx.dataset.data[ctx.dataIndex] > 0,
        font: {
          family: 'Montserrat',
          weight: 'bold',
          color: colors.deepBlue,
        },
        formatter: (value, ctx) => {
          const sum = ctx.chart.data.datasets[0].data.reduce(
            (total, ctxData) => total + ctxData,
            0,
          )

          if (sum === 0) {
            return ''
          }

          return (value * 100 / sum).toFixed(0) + '%'
        },
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
  },
}

const Badges = ({ members }) => {
  const canvas = useRef(null)
  const badgesArray = members.map(member => (
    member.monthlyStatistic.badge
  ))
  const ownedBadges = badges
    .map(badge => ({
      ...badge,
      count: badgesArray.filter(type => type === badge.type).length,
    }))
    .filter(({ count }) => count > 0)

  useEffect(() => {
    Chart.register(PieController, ArcElement)

    const chart = new Chart(canvas.current.getContext('2d'), {
      ...data,
      data: {
        ...data.data,
        labels: ownedBadges.map(({ name }) => name),
        datasets: [{
          ...data.data.datasets[0],
          backgroundColor: ownedBadges.map(({ color }) => color),
          data: ownedBadges.map(({ count }) => count),
        }],
      },
    })

    return () => chart.destroy()
  }, [badgesArray.join(',')])

  return (
    <>
      <Title>
        <h3>Répartition des badges</h3>
      </Title>
      <Wrapper>
        <LegendWrapper>
          { ownedBadges.map(({ type, name, color, range }) => (
            <Legend key={ type }>
              <Dot $color={ color } />
              <LegendTextWrapper>
                <LegendName>
                  { name }
                </LegendName>
                <LegendLabel>
                  { range }
                </LegendLabel>
              </LegendTextWrapper>
            </Legend>
          )) }
        </LegendWrapper>
        <ChartContainer>
          <canvas height="400"
            id="badges"
            ref={ canvas }
          >
          </canvas>
        </ChartContainer>
      </Wrapper>
    </>
  )
}

Badges.propTypes = {
  members: PropTypes.arrayOf(PropTypes.shape({
    monthlyStatistic: PropTypes.shape({
      badge: PropTypes.string,
    }),
  })).isRequired,
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`
const ChartContainer = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 65%;
`

const Title = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  h3 {
    font-family: Montserrat;
    color: ${colors.deepBlue};
  }
`
const LegendTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const LegendWrapper = styled.div`
  display: grid;
  align-self: flex-start;
  padding-top: 1.5rem;
  gap: 0.75rem 0.5rem;
`

const Legend = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const Dot = styled.span`
  display: inline-block;
  flex: 0 0 auto;
  width: 1.25rem;
  height: 1.25rem;
  background-color: ${({ $color }) => $color};
  border-radius: 100%;
`

const LegendLabel = styled.span`
  display: flex;
  flex-direction: row;
  margin-left: 0.5rem;
  font-family: 'brother-1816';
  font-size: 0.6rem;
  color: ${colors.deepBlue};
  opacity: 0.5;
`

const LegendName = styled.span`
  margin-bottom: 0.25rem;
  margin-left: 0.5rem;
  font-family: 'brother-1816';
  font-size: 0.875rem;
  color: ${colors.deepBlue};
`

export default Badges
